import { Flex, Text, xcss } from '@atlaskit/primitives';
import Skeleton from '@atlaskit/skeleton';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-resource-router';

import { SettingsList } from '../../components/settings-list';
import { ROWS_PER_PAGE } from '../../components/types';
import { useGithubRepositoryList } from '../hooks';
import type { CodeReviewerSetting } from '../types';
import { createKey } from '../utils';

import { CodeReviewerFlags, type CRFlag } from './code-reviewer-flags';
import { RepositoryToggle } from './toggle';

const styles = xcss({ height: '50px' });

const LoadingCell = ({ width }: { width: string }) => (
	<Flex xcss={styles} alignItems="center" justifyContent="start">
		<Skeleton width={width} height="25px" isShimmering borderRadius="25px" />
	</Flex>
);

const loadingRow = (selectedOrg: GH.Organisation, idx: number) => ({
	key: `${selectedOrg.owner}-repo-${idx}`,
	cells: [
		{
			key: `repo-loading-cell-1-${idx}`,
			content: <LoadingCell width={`${Math.floor(Math.random() * 100)}%`} />,
		},
		{
			key: `repo-loading-cell-2-${idx}`,
			content: <LoadingCell width={`${Math.floor(Math.random() * 100)}%`} />,
		},
		{
			key: `repo-loading-cell-3-${idx}`,
			content: <LoadingCell width="80%" />,
		},
	],
});

const newRow = (
	owner: string,
	repo: GH.Repository,
	idx: number,
	orgSetting: CodeReviewerSetting | null,
	superToggle: (repoId: string, newValue: boolean) => void,
) => ({
	key: `${owner}-repo-${idx}`,
	cells: [
		{
			key: createKey(repo.repoName),
			content: (
				<Flex xcss={styles} alignItems="center">
					<Text>{repo.repoName}</Text>
				</Flex>
			),
		},
		{
			key: createKey(repo.repoUrl),
			content: <Link href={repo.repoUrl}>{repo.repoUrl}</Link>,
		},
		{
			key: `${repo.repoId}-action-cell`,
			content: (
				<RepositoryToggle repository={repo} orgSetting={orgSetting} superToggle={superToggle} />
			),
		},
	],
});

const repoHead = {
	cells: [
		{
			key: 'name-head-cell',
			content: 'Name',
			isSortable: false,
			width: 20,
		},
		{
			key: 'url-head-cell',
			content: 'URL',
			isSortable: false,
			width: 55,
		},
		{
			key: 'actions-head-cell',
			content: 'Code Reviewer status',
			isSortable: false,
			width: 25,
		},
	],
};

type GithubRepositoryListProps = {
	selectedOrg: GH.Organisation;
};
export const GithubRepositoryList = ({ selectedOrg }: GithubRepositoryListProps) => {
	const [rowsPerPage, setRowsPerPage] = useState<number>(ROWS_PER_PAGE);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const pageCache = useRef<number>(1);
	const { repoList, loading, error, searchRepos, updateRepoSettingInList } =
		useGithubRepositoryList({
			subscriptionId: selectedOrg.subscriptionId,
			page: currentPage,
			rowsPerPage,
		});
	const [flags, setFlags] = useState<CRFlag[]>([]);

	const superToggle = useCallback(
		(repoId: string, newValue: boolean) => {
			const flagToAdd: CRFlag = {
				id: flags.length + 1,
				title: newValue ? 'Enabled Code Reviewer' : 'Disabled Code Reviewer',
				description: 'This might take up to 15 minutes',
			};

			setFlags((current) => [...current, flagToAdd]);

			updateRepoSettingInList(repoId, newValue);
		},
		[flags.length, updateRepoSettingInList],
	);

	const onSearch = useCallback(
		(input: string) => {
			if (input === '') {
				setRowsPerPage(ROWS_PER_PAGE);
				setCurrentPage(pageCache.current);
				return;
			}

			setRowsPerPage(selectedOrg.totalNumberOfRepos);
			setCurrentPage(1);

			searchRepos(input);
		},
		[selectedOrg.totalNumberOfRepos, searchRepos],
	);

	const handleSetCurrentPage = (n: number) => {
		setCurrentPage(n);
		pageCache.current = n;
	};

	/**
	Explanation of logic:
	 The DynamicTable component used by SettingsList requires the list of rows to be complete for
	 pagination to work correctly. For this reason, we need to provide it with a list of the correct
	 size, even though we don't have those repos loaded from the hook.

	 This block will provide the table with a list of rows that is the correct size, with the current
	 page offset into the correct location in that list to display that page correctly. The pages are
	 cached in the useGithubRepositoryList hook for quick use next time.
	* */
	const startingIndex = (currentPage - 1) * ROWS_PER_PAGE;
	const renderRows = useMemo(() => {
		if (rowsPerPage === selectedOrg.totalNumberOfRepos) {
			return repoList.map((r, idx) =>
				newRow(selectedOrg.owner, r, idx, selectedOrg.autoreviewSettings, superToggle),
			);
		}

		return Array.from({ length: selectedOrg.totalNumberOfRepos }, (_, idx) => {
			const repo: GH.Repository | undefined = repoList[idx - startingIndex];
			if (repo) {
				return newRow(selectedOrg.owner, repo, idx, selectedOrg.autoreviewSettings, superToggle);
			}
			return loadingRow(selectedOrg, idx);
		});
	}, [rowsPerPage, selectedOrg, repoList, superToggle, startingIndex]);

	return (
		<>
			<SettingsList
				headingText="Repositories"
				error={error}
				loading={loading}
				tableHead={repoHead}
				tableRows={renderRows}
				currentPage={currentPage}
				setCurrentPage={handleSetCurrentPage}
				rowsPerPage={rowsPerPage}
				onSearch={onSearch}
			/>
			<CodeReviewerFlags flags={flags} setFlags={setFlags} />
		</>
	);
};
